import type { StateCreator } from 'zustand';
import type { CardTag } from '@my-fullstack-app/itinerary-types';
import { markTripDirty } from '../sync-bridge';
import type { ItineraryState } from '../itinerary-store';
import { track } from '../../lib/analytics';

export interface NoteMeta {
  /** ISO timestamp of the most recent edit. */
  updatedAt: string;
}

export interface NotesSlice {
  notesMap: Record<string, string>;
  notesMetaMap: Record<string, NoteMeta>;
  setNote: (tripId: string, entityId: string, content: string | undefined) => void;
  tagsMap: Record<string, CardTag>;
  setTag: (tripId: string, entityId: string, tag: CardTag | undefined) => void;
}

export const createNotesSlice: StateCreator<ItineraryState, [], [], NotesSlice> = (set, get) => ({
  notesMap: {},
  notesMetaMap: {},
  tagsMap: {},

  setNote: (tripId, entityId, content) => {
    get()._capture();
    const hadContent = !!get().notesMap[entityId];
    const nextContent = (content ?? '').trim();
    const hasContent = nextContent.length > 0;
    set((s) => {
      const next = { ...s.notesMap };
      const meta = { ...s.notesMetaMap };
      if (!content || content.trim() === '') {
        delete next[entityId];
        delete meta[entityId];
      } else {
        next[entityId] = content;
        meta[entityId] = { updatedAt: new Date().toISOString() };
      }
      return { notesMap: next, notesMetaMap: meta };
    });
    // Mark 'days' dirty too so syncTrip's flush-time injectNotes can fold
    // the entity-level note back into the days payload before sending.
    // Top-level trip notes flush on the 'notes' field; entity notes
    // (place / day / transport / custom) flush via the 'days' field.
    markTripDirty(tripId, ['notes', 'days', 'stays', 'journeys']);
    // Polymorphic — entityId may be a trip, day, place, transport, custom,
    // journey, or stay. The dashboard joins on tripId + entityId when
    // splitting by entity kind matters.
    if (hasContent) {
      track('note.saved', { tripId, entityId, hadContent, length: nextContent.length });
    } else if (hadContent) {
      track('note.cleared', { tripId, entityId });
    }
  },

  setTag: (tripId, entityId, tag) => {
    get()._capture();
    set((s) => {
      const next = { ...s.tagsMap };
      if (!tag) {
        delete next[entityId];
      } else {
        next[entityId] = tag;
      }
      return { tagsMap: next };
    });
    markTripDirty(tripId, ['tags']);
  },
});
